import { IntakeData } from "../pages/Index";

const getBackendUrl = () =>
  import.meta.env.VITE_JEMA_BACKEND_URL || "http://localhost:3000";

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    let errorMessage = "An error occurred";
    try {
      const errorData = await response.json();
      if (errorData.error) {
        if (typeof errorData.error === "string") {
          errorMessage = errorData.error;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.error.code) {
          errorMessage = errorData.error.code;
        } else {
          errorMessage = JSON.stringify(errorData.error);
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

type StartIntakeApiResponse = {
  data: {
    formUid: string;
    referenceUid: string;
    specialty: string;
    status: string;
    patientCreated: boolean;
    requiresVerification: boolean;
    patientId?: string;
  };
};

type MagicLinkChallenge = {
  method: string;
  challengeId?: string;
  expiresAt?: string;
  requiresOtpFallback: boolean;
  alreadySent?: boolean;
  sentAt?: string;
};

type OtpChallenge = {
  challengeId: string;
  method: string;
  expiresAt: string;
  message: string;
};

type FinalizeIntakeApiResponse = {
  data: {
    intakeForm: any;
    serviceRequest?: any;
    magicLink?: MagicLinkChallenge;
    otpChallenge?: OtpChallenge;
  };
};

export type MediaUploadResponse = {
  data: {
    uploadUrl: string;
    bucketName: string;
    storagePath: string;
    expiresIn: number;
  };
};

export type MediaRegistrationResponse = {
  data?: {
    media?: {
      id: string;
      mediaType: string;
      status?: string;
      downloadUrl?: string;
      downloadUrlExpiresIn?: number;
      photoPosition?: string;
      photoSequence?: number;
    };
  };
};

type BatchPresignUploadRequest = {
  clientReference: string;
  mimeType: string;
  size: number;
  originalFilename?: string;
};

type BatchPresignUploadResponse = {
  data: {
    uploads: Array<{
      clientReference: string;
      uploadUrl: string;
      bucketName: string;
      storagePath: string;
      expiresIn: number;
    }>;
  };
};

type RegisteredMedia = NonNullable<MediaRegistrationResponse["data"]>["media"];

type BatchMediaRegistrationResponse = {
  data: {
    items: Array<{
      clientReference: string;
      media: RegisteredMedia;
    }>;
  };
};

type BatchMediaRegistrationRequestItem = {
  clientReference: string;
  mediaType: "photo";
  s3BucketName: string;
  s3StoragePath: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  photoPosition?: string;
  photoSequence?: number;
  replaceMediaId?: string;
  metadata?: Record<string, unknown>;
};

type VerificationResponse = {
  data: {
    success: boolean;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
    user: {
      id: string;
      email: string;
      role: string;
      onboardingComplete: boolean;
    };
    intakeForm?: any;
    serviceRequest?: any;
  };
};

const MEDICAL_QUESTION_ORDER = [
  "preExistingConditions",
  "currentMedications",
  "allergies",
  "majorSurgeries",
  "dentalProcedureType",
  "dentalDiscomfort",
  "mobilityIssues",
  "dietaryRestrictions",
  "travelConcerns",
  "smokingHabits",
  "travelAlone",
  "budgetExpectations",
  "personalInterest",
] as const satisfies readonly (keyof IntakeData)[];

// Mirrors the DynamicQuestionTree so API payload preserves question order.
const DENTAL_PROCEDURE_DYNAMIC_ORDER: Record<
  string,
  readonly (keyof IntakeData)[]
> = {
  orthodontics: [
    "orthodonticHistory",
    "orthodonticGoals",
    "treatmentDuration",
    "appliancePreference",
  ],
  implants: [
    "missingTeethCount",
    "missingTeethDuration",
    "boneGraftHistory",
    "implantExpectations",
  ],
  cosmetic: [
    "cosmeticGoals",
    "previousCosmetic",
    "smileConfidence",
    "socialImpact",
  ],
  restoration: [
    "reconstructionReason",
    "currentPainLevel",
    "functionIssues",
    "treatmentPriority",
  ],
  veneers: [
    "veneerGoals",
    "teethToTreat",
    "currentTeethCondition",
    "previousCosmetic",
  ],
  crowns: [
    "crownReason",
    "numberOfCrowns",
    "crownMaterial",
    "urgencyLevel",
  ],
  whitening: [
    "whiteningGoal",
    "stainingType",
    "previousWhitening",
    "sensitivityConcern",
  ],
  other: [
    "lastDentalVisit",
    "dentalConcerns",
    "treatmentInterest",
  ],
};

const OPTIONAL_EXPERIENCE_KEYS = [
  "selectedServices",
  "selectedTours",
  "selectedExperience",
  "additionalServicesCost",
] as const satisfies readonly (keyof IntakeData)[];

const normalizeAnswer = (
  value: unknown,
): string | number | boolean | string[] | Record<string, unknown> | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? (value as string[]) : null;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length > 0 ? (value as Record<string, unknown>) : null;
  }

  return null;
};

export const buildFormPayload = (data: IntakeData) => {
  const identity = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone || undefined,
    dateOfBirth: data.dateOfBirth || undefined,
    city: data.city || undefined,
    state: data.state || undefined,
    country: data.country || undefined,
  };

  const responses: Array<{
    questionId: string;
    answer: string | number | boolean | string[] | Record<string, unknown> | null;
  }> = [];

  const pushResponse = (
    questionId: keyof IntakeData,
    includeWhenNull = true,
  ) => {
    const answer = normalizeAnswer(data[questionId]);
    if (answer === null && !includeWhenNull) {
      return;
    }

    responses.push({ questionId, answer });
  };

  MEDICAL_QUESTION_ORDER.forEach((questionId) => {
    pushResponse(questionId);
  });

  const dynamicQuestionOrder =
    DENTAL_PROCEDURE_DYNAMIC_ORDER[
      data.dentalProcedureType as keyof typeof DENTAL_PROCEDURE_DYNAMIC_ORDER
    ] || [];

  dynamicQuestionOrder.forEach((questionId) => {
    pushResponse(questionId);
  });

  OPTIONAL_EXPERIENCE_KEYS.forEach((questionId) => {
    pushResponse(questionId, false);
  });

  return {
    identity,
    specialty: "dentistry" as const,
    responses,
    portalContext: "patient" as const,
  };
};

export const patientIntakeApi = {
  async submitIntake(data: IntakeData) {
    const payload = buildFormPayload(data);

    const startResponse = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: payload }),
      },
    );

    const startResult = (await handleApiResponse(
      startResponse,
    )) as StartIntakeApiResponse;

    // Don't finalize here - let the verification step handle sending the magic link
    // This prevents duplicate emails being sent
    return { data: startResult.data } as any;
  },

  async finalizeIntake(formUid: string) {
    const finalizeResponse = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/finalize`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submittedAt: new Date().toISOString() }),
      },
    );

    return handleApiResponse(
      finalizeResponse,
    ) as Promise<FinalizeIntakeApiResponse>;
  },

  async getPhotoUploadUrl(
    formUid: string,
    mimeType: string,
    size: number,
    originalFilename: string,
  ): Promise<MediaUploadResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/presign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mimeType, size, originalFilename }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhotoToCloud(uploadUrl: string, file: File) {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!response.ok) {
      throw new Error("Failed to upload photo to cloud storage");
    }
  },

  async registerPhoto(
    formUid: string,
    bucketName: string,
    storagePath: string,
    originalFilename: string,
    mimeType: string,
    fileSize: number,
    photoPosition?: string,
    photoSequence?: number,
    replaceMediaId?: string,
  ): Promise<MediaRegistrationResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaType: "photo",
          s3BucketName: bucketName,
          s3StoragePath: storagePath,
          originalFilename,
          mimeType,
          fileSize,
          photoPosition,
          photoSequence,
          replaceMediaId,
        }),
      },
    );

    return handleApiResponse(response);
  },

  async getBatchPhotoUploadUrls(
    formUid: string,
    files: BatchPresignUploadRequest[],
  ): Promise<BatchPresignUploadResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/batch/presign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files }),
      },
    );

    return handleApiResponse(response);
  },

  async registerMediaBatch(
    formUid: string,
    items: BatchMediaRegistrationRequestItem[],
  ): Promise<BatchMediaRegistrationResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/batch`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhoto(
    formUid: string,
    file: File,
    options?: {
      photoPosition?: string;
      photoSequence?: number;
      replaceMediaId?: string;
    },
  ): Promise<MediaRegistrationResponse> {
    const presignResponse = await this.getPhotoUploadUrl(
      formUid,
      file.type,
      file.size,
      file.name,
    );
    await this.uploadPhotoToCloud(presignResponse.data.uploadUrl, file);
    return this.registerPhoto(
      formUid,
      presignResponse.data.bucketName,
      presignResponse.data.storagePath,
      file.name,
      file.type,
      file.size,
      options?.photoPosition,
      options?.photoSequence,
      options?.replaceMediaId,
    );
  },

  async sendVerificationCode(email: string, formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/send-verification-code`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, method: "otp_email", formUid }),
      },
    );

    return handleApiResponse(response) as Promise<{ data: OtpChallenge }>;
  },

  async verifyEmail(
    email: string,
    token: string,
    formUid: string,
    challengeId?: string,
  ) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/verify-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, challengeId, formUid }),
      },
    );

    return handleApiResponse(response) as Promise<VerificationResponse>;
  },

  async startAuth(payload: {
    email: string;
    formUid: string;
    referenceUid: string;
    forceResend?: boolean;
  }) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/auth/start`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    return handleApiResponse(response) as Promise<{ data: MagicLinkChallenge }>;
  },

  async completeMagicLinkCallback(body: {
    accessToken: string;
    refreshToken?: string;
    formUid: string;
    referenceUid: string;
  }) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/auth/magic-link/callback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    return handleApiResponse(response);
  },

  async getIntakeStatus(formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/status?includeMedia=true`,
    );

    return handleApiResponse(response);
  },

  async getIntakeFormByEmail(email: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/by-email/${encodeURIComponent(email)}`,
    );

    return handleApiResponse(response);
  },

  async updateIntake(formUid: string, data: IntakeData) {
    const payload = buildFormPayload(data);

    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: payload }),
      },
    );

    return handleApiResponse(response);
  },

  async getFormMedia(formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media`,
    );

    return handleApiResponse(response);
  },

  async deleteForm(formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}`,
      {
        method: "DELETE",
      },
    );

    return handleApiResponse(response);
  },
};
